import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { $generateHtmlFromNodes, $generateNodesFromDOM } from '@lexical/html';
import { $getRoot, $insertNodes, $getSelection, $isRangeSelection, EditorState, COMMAND_PRIORITY_HIGH, PASTE_COMMAND } from 'lexical';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { ListNode, ListItemNode } from '@lexical/list';
import { LinkNode, AutoLinkNode } from '@lexical/link';
import { BasicToolbar } from './BasicToolbar';
import { ImageNode, $createImageNode } from './ImageNode';
import './rte.css';

/**
 * Sanitize HTML by removing all style attributes to keep output clean and raw
 */
function sanitizeHTML(html: string): string {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  
  // Find all elements with style attributes and remove them
  const elementsWithStyle = doc.querySelectorAll('[style]');
  elementsWithStyle.forEach((element) => {
    element.removeAttribute('style');
  });
  
  // Return cleaned HTML without any inline styles
  return doc.body.innerHTML;
}

// Props interface matching ReactQuill API
export interface LexicalQuillCompatProps {
  // Content props (HTML strings)
  value?: string;
  defaultValue?: string;
  onChange?: (html: string) => void;

  // Appearance props
  theme?: 'snow' | 'bubble';
  readOnly?: boolean;
  placeholder?: string;
  className?: string;
  id?: string;

  // Custom handlers
  onPasteHTML?: (html: string, event: ClipboardEvent) => void;
  onImageUpload?: (file: File) => Promise<string>;
  onPasteImage?: (file: File) => Promise<string>;

  // Toolbar options
  showImageUpload?: boolean;

  // Legacy prop (ignored but accepted for compatibility)
  modules?: any;
}

// Ref interface matching ReactQuill API
export interface LexicalQuillCompatRef {
  getText: () => Promise<string>;
  getHTML: () => Promise<string>;
  setHTML: (html: string) => void;
  focus: () => void;
}

// Plugin to initialize HTML content on mount
function HTMLInitializer({ html }: { html?: string }) {
  const [editor] = useLexicalComposerContext();
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (!html || initialized) return;

    editor.update(() => {
      const parser = new DOMParser();
      const dom = parser.parseFromString(html, 'text/html');
      const nodes = $generateNodesFromDOM(editor, dom);
      const root = $getRoot();
      root.clear();
      $insertNodes(nodes);
    });

    setInitialized(true);
  }, [editor, html, initialized]);

  return null;
}

// Plugin to sync controlled value prop changes
function ControlledHTMLSync({ value, lastPushedValue }: { value?: string; lastPushedValue: React.MutableRefObject<string | undefined> }) {
  const [editor] = useLexicalComposerContext();
  const lastValueRef = useRef<string | undefined>(value);

  useEffect(() => {
    // Skip if value is undefined, hasn't changed, or is the same as what we just pushed
    if (value === undefined || value === lastValueRef.current || value === lastPushedValue.current) {
      return;
    }

    editor.update(() => {
      const parser = new DOMParser();
      const dom = parser.parseFromString(value, 'text/html');
      const nodes = $generateNodesFromDOM(editor, dom);
      const root = $getRoot();
      root.clear();
      $insertNodes(nodes);
    });

    lastValueRef.current = value;
  }, [editor, value, lastPushedValue]);

  return null;
}

// Plugin to intercept paste events and call custom handler
function PasteInterceptor({
  onPasteHTML,
  onPasteImage
}: {
  onPasteHTML?: (html: string, event: ClipboardEvent) => void;
  onPasteImage?: (file: File) => Promise<string>;
}) {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    return editor.registerCommand(
      PASTE_COMMAND,
      (event: ClipboardEvent) => {
        const clipboardData = event.clipboardData;
        if (!clipboardData) return false;

        // Check for image files first
        const files = clipboardData.files;
        if (files && files.length > 0 && onPasteImage) {
          const imageFile = Array.from(files).find(file => file.type.startsWith('image/'));

          if (imageFile) {
            event.preventDefault();

            // Handle image paste
            onPasteImage(imageFile).then((imageUrl) => {
              editor.update(() => {
                const selection = $getSelection();
                if ($isRangeSelection(selection)) {
                  const imageNode = $createImageNode({
                    src: imageUrl,
                    altText: imageFile.name,
                    width: '100%',
                  });
                  selection.insertNodes([imageNode]);
                }
              });
            }).catch((error) => {
              console.error('Failed to upload pasted image:', error);
            });

            return true; // Command handled
          }
        }

        // Get HTML and plain text from clipboard
        const html = clipboardData.getData('text/html') || '';
        const plainText = clipboardData.getData('text/plain') || '';

        // If there's HTML content, sanitize and insert it (preserving structure)
        if (html) {
          event.preventDefault();

          // Sanitize: remove inline styles but keep structural tags (b, i, u, ul, ol, li, h1-h6, br, p)
          const sanitized = sanitizeHTMLForPaste(html);

          // Call custom handler if provided
          if (onPasteHTML) {
            onPasteHTML(sanitized, event);
            if (event.defaultPrevented) return true;
          }

          // Insert the sanitized HTML into the editor
          editor.update(() => {
            const parser = new DOMParser();
            const dom = parser.parseFromString(sanitized, 'text/html');
            const nodes = $generateNodesFromDOM(editor, dom);
            const selection = $getSelection();
            if ($isRangeSelection(selection)) {
              selection.insertNodes(nodes);
            }
          });

          return true; // Command handled
        }

        // If only plain text with newlines, convert \n to paragraphs
        if (plainText && plainText.includes('\n')) {
          event.preventDefault();

          // Convert plain text newlines to HTML paragraphs
          const htmlFromPlain = plainText
            .split('\n')
            .map(line => line.trim() === '' ? '<br>' : `<p>${escapeHTML(line)}</p>`)
            .join('');

          editor.update(() => {
            const parser = new DOMParser();
            const dom = parser.parseFromString(htmlFromPlain, 'text/html');
            const nodes = $generateNodesFromDOM(editor, dom);
            const selection = $getSelection();
            if ($isRangeSelection(selection)) {
              selection.insertNodes(nodes);
            }
          });

          return true; // Command handled
        }

        // Allow default paste behavior for simple single-line text
        return false;
      },
      COMMAND_PRIORITY_HIGH
    );
  }, [editor, onPasteHTML, onPasteImage]);

  return null;
}

/**
 * Sanitize HTML for paste: remove styles, classes, and non-structural attributes
 * but keep allowed tags: b, strong, i, em, u, ul, ol, li, h1-h6, p, br, a, span
 */
function sanitizeHTMLForPaste(html: string): string {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  
  // Allowed tags (structural and formatting only)
  const ALLOWED_TAGS = new Set([
    'P', 'BR', 'B', 'STRONG', 'I', 'EM', 'U', 'UL', 'OL', 'LI',
    'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'A', 'SPAN', 'DIV', 'BLOCKQUOTE'
  ]);

  // Walk the tree and clean up
  const walkAndClean = (node: Element) => {
    // Remove all style attributes
    node.removeAttribute('style');
    node.removeAttribute('class');
    
    // Process children
    const children = Array.from(node.children);
    for (const child of children) {
      if (!ALLOWED_TAGS.has(child.tagName)) {
        // Replace disallowed tag with its text content
        const text = doc.createTextNode(child.textContent || '');
        child.replaceWith(text);
      } else {
        walkAndClean(child);
      }
    }
  };

  walkAndClean(doc.body);
  return doc.body.innerHTML;
}

/**
 * Escape HTML special characters for safe insertion
 */
function escapeHTML(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

// Main component
const LexicalQuillCompat = forwardRef<LexicalQuillCompatRef, LexicalQuillCompatProps>(
  (props, ref) => {
    const {
      value,
      defaultValue,
      onChange,
      theme = 'snow',
      readOnly = false,
      placeholder = 'Write something...',
      className = '',
      id,
      onPasteHTML,
      onImageUpload,
      onPasteImage,
      showImageUpload = false,
      modules,
    } = props;

    const editorRef = useRef<any>(null);
    const lastPushedValueRef = useRef<string | undefined>(undefined);
    const isControlled = value !== undefined;
    const initialHTML = isControlled ? value : defaultValue;

    // Initial editor configuration
    const initialConfig = {
      namespace: 'LexicalQuillCompat',
      theme: {},
      onError: (error: Error) => {
        console.error('Lexical error:', error);
      },
      editable: !readOnly,
      nodes: [
        HeadingNode,
        QuoteNode,
        ListNode,
        ListItemNode,
        LinkNode,
        AutoLinkNode,
        ImageNode,
      ],
    };

    // Handle editor state changes
    const handleChange = (editorState: EditorState) => {
      if (!onChange) return;

      editorState.read(() => {
        let html = $generateHtmlFromNodes(editorRef.current);
        // Sanitize HTML to remove empty style attributes
        html = sanitizeHTML(html);
        lastPushedValueRef.current = html;
        onChange(html);
      });
    };

    // Expose ref methods
    useImperativeHandle(ref, () => ({
      getText: async (): Promise<string> => {
        if (!editorRef.current) return '';

        return new Promise((resolve) => {
          editorRef.current.getEditorState().read(() => {
            const root = $getRoot();
            const text = root.getTextContent();
            resolve(text);
          });
        });
      },

      getHTML: async (): Promise<string> => {
        if (!editorRef.current) return '';

        return new Promise((resolve) => {
          editorRef.current.getEditorState().read(() => {
            let html = $generateHtmlFromNodes(editorRef.current);
            // Sanitize HTML to remove empty style attributes
            html = sanitizeHTML(html);
            resolve(html);
          });
        });
      },

      setHTML: (html: string): void => {
        if (!editorRef.current) return;

        editorRef.current.update(() => {
          const parser = new DOMParser();
          const dom = parser.parseFromString(html, 'text/html');
          const nodes = $generateNodesFromDOM(editorRef.current, dom);
          const root = $getRoot();
          root.clear();
          $insertNodes(nodes);
        });
      },

      focus: (): void => {
        if (!editorRef.current) return;
        editorRef.current.focus();
      },
    }));

    // Store editor reference
    const EditorRefPlugin = () => {
      const [editor] = useLexicalComposerContext();
      editorRef.current = editor;
      return null;
    };

    const containerClass = `rte rte-${theme} ${readOnly ? 'rte-readonly' : ''} ${className}`.trim();

    return (
      <LexicalComposer initialConfig={initialConfig}>
        <div className={containerClass} id={id}>
          <EditorRefPlugin />
          <HTMLInitializer html={initialHTML} />
          {isControlled && <ControlledHTMLSync value={value} lastPushedValue={lastPushedValueRef} />}
          <OnChangePlugin onChange={handleChange} />
          <HistoryPlugin />
          <ListPlugin />
          <LinkPlugin />
          <PasteInterceptor onPasteHTML={onPasteHTML} onPasteImage={onPasteImage} />

          {theme === 'snow' && !readOnly && (
            <BasicToolbar
              showImageUpload={showImageUpload}
              onImageUpload={onImageUpload}
            />
          )}

          <RichTextPlugin
            contentEditable={
              <div className="rte-content">
                <ContentEditable />
              </div>
            }
            placeholder={
              <div className="rte-placeholder">{placeholder}</div>
            }
            ErrorBoundary={(props) => <div className="rte-error">An error occurred</div>}
          />
        </div>
      </LexicalComposer>
    );
  }
);

LexicalQuillCompat.displayName = 'LexicalQuillCompat';

export default LexicalQuillCompat;
